import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import { formataDinheiro } from "@/utils/ForAll";
import type { CatalogoModulo } from "@/types/planos";
import { metaDoModulo } from "@/Logigate/components/planos/metaDoModulo";
import { EVENTO_ABRIR_MODULO } from "@/Logigate/componentes/planos/loja/LojaDeModulos";
import { publish } from "@/utils/events";

/** Desliza até à loja (secção «Planos e Preços») e abre lá a ficha do módulo. */
function verNaLoja(moduloId: number) {
    document.querySelector("#precos")?.scrollIntoView({ behavior: "smooth" });
    publish(EVENTO_ABRIR_MODULO, { id: moduloId });
}

interface Props {
    modulos: CatalogoModulo[];
}

export default function ModulesShowcase({ modulos }: Props) {
    return (
        <section id="modulos" className="py-20 lg:py-28 bg-slate-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="text-center mb-16">
                    <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Módulos Disponíveis</h2>
                    <p className="text-lg text-white/50 max-w-2xl mx-auto">Escolhe entre {modulos.length} módulos especializados para as necessidades do teu negócio.</p>
                </motion.div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {modulos.map((modulo, index) => {
                        const meta = metaDoModulo(modulo.id);
                        const maisBarato = modulo.planos.length ? Math.min(...modulo.planos.map((p) => p.preco_final)) : null;
                        const dependencias = modulo.depende_de.map((id) => modulos.find((m) => m.id === id)?.nome).filter(Boolean);

                        return (
                            <motion.div
                                key={modulo.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: index * 0.05 }}
                                className="group relative bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-2xl p-6 transition-all duration-300"
                            >
                                <div className={`absolute inset-0 rounded-2xl bg-linear-to-br ${meta.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />

                                <div className="relative">
                                    <div className={`w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-4 ${meta.colorEscuro}`}>
                                        <Icon icon={modulo.icone || meta.icon} width={26} />
                                    </div>

                                    <h3 className="text-lg font-semibold text-white mb-2">{modulo.nome}</h3>

                                    <p className="text-sm text-white/50 mb-4 line-clamp-2">{modulo.descricao}</p>

                                    {dependencias.length > 0 && <p className="text-xs text-white/40 mb-3">Em conjunto com {dependencias.join(", ")}.</p>}

                                    <div className="flex items-center justify-between">
                                        {modulo.pelo_uso ? (
                                            // Os módulos pagos pelo uso não têm planos: nunca «Grátis».
                                            <span className="text-sm text-blue-400 font-medium">Pago pelo uso · {modulo.pelo_uso.texto}</span>
                                        ) : maisBarato !== null && maisBarato > 0 ? (
                                            <span className="text-sm text-blue-400 font-medium">A partir de {formataDinheiro(maisBarato, "Kz")}/mês</span>
                                        ) : maisBarato === 0 ? (
                                            <span className="text-sm text-blue-400 font-medium">Grátis</span>
                                        ) : null}

                                        <button
                                            type="button"
                                            onClick={() => verNaLoja(modulo.id)}
                                            className="text-xs text-white/40 hover:text-white/70 transition-colors flex items-center gap-1"
                                        >
                                            Ver na loja
                                            <Icon icon="mdi:arrow-right" width={14} />
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
